import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { AsyncTypeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css'
import './BaseSearch.css'
import Spinner, {SpinnerSize} from "./Fundamentals/Spinner/Spinner";
import BoldMatchingText from "./Fundamentals/BoldMatchingText";

const BaseSearch = forwardRef(({ attributes, results, id }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const typeaheadRef = useRef(null);
    const latestSearchRef = useRef(0);

    useImperativeHandle(ref, () => ({
        focus: () => {
            typeaheadRef.current?.focus();
        },
        clear: () => {
            typeaheadRef.current?.clear();
        }
    }));

    function handleOnSearch(query) {
        if (!query) {
            return;
        }
        setSearchQuery(query);
        setIsLoading(true);
        const searchId = ++latestSearchRef.current;

        Promise.resolve(attributes.onSearchAttr(query))
            .finally(() => {
                if (searchId === latestSearchRef.current) {
                    setIsLoading(false);
                }
            });
    }

    function handleOnChange(options) {
        if (options && options[0]) {
            attributes.onChangeAttr(options[0]);
        }
    }

    function handleOnClear(input) {
        if (!input) {
            latestSearchRef.current++;
            setIsLoading(false);
            attributes.onClearAttr?.();
            attributes.onChangeAttr('')
        }
    }

    const renderMenu = (results, menuProps, props) => {
        // Typeahead merges these into menuProps but Menu doesn't strip them before
        // spreading onto its DOM <div> — drop them to avoid unknown-prop warnings.
        const cleanMenuProps = { ...menuProps };
        delete cleanMenuProps.newSelectionPrefix;
        delete cleanMenuProps.paginationText;
        delete cleanMenuProps.renderMenuItemChildren;

        if (isLoading) {
            return (
                <Menu {...cleanMenuProps}>
                    <div className="search-spinner-container">
                        <Spinner size={SpinnerSize.SMALL} />
                    </div>
                </Menu>
            );
        }

        const items = results.map((result, index) => {
            let content;
            if (props.renderMenuItemChildren) {
                content = props.renderMenuItemChildren(result, props, index);
            } else {
                const labelText = props.labelKey(result);
                content = <BoldMatchingText text={labelText} query={searchQuery} />;
            }

            return (
                <MenuItem key={index} option={result} position={index}>
                    {content}
                </MenuItem>
            );
        });

        return <Menu {...cleanMenuProps}>{items}</Menu>;
    };

    let finalAttributes = {
        labelKey: option => `${option.id}`,
        delay: 400,
        minLength: 2,
        highlight: true,
        useCache: false,
        options: results || [],
        onSearch: handleOnSearch,
        renderMenu: renderMenu,
        promptText: 'Type to search..',
        emptyLabel: 'No matches found.',
        className: '',
        onChange: handleOnChange,
        onInputChange: handleOnClear,
        align: 'left',
        isLoading: isLoading,
        id: id || "baseSearch",
        filterBy: () => true, // This is to disable client side filtering
        ...attributes
    };
    
    return (
        <AsyncTypeahead 
            ref={typeaheadRef} 
            isLoading={isLoading} 
            {...finalAttributes} 
        />
    );
});


export default BaseSearch;